/* eslint-disable unicorn/no-array-reduce */

import { makeAutoObservable } from 'mobx';
import * as semver from 'semver';
import Version, { VersionConstructorDto, VersionDto } from './Version';
import VersionFile from './VersionFile';

export default class Versions {
  items: Version[] = [];

  private fileCounter = 0;

  constructor(
    versions: VersionConstructorDto[] = [],
    private readonly forceEditMode = false,
    private readonly onChange?: (versions: Versions) => Promise<void>,
  ) {
    makeAutoObservable(this, { onChange: false } as any, { autoBind: true });

    this.items = versions.map(
      (dto) => new Version({ forceEditMode, ...dto }, this),
    );

    this.ensureEmptyFileInTheBottom();

    if (this.items.length === 0) {
      this.addEmptyVersion();
    }
  }

  get allowsAddVersion() {
    return !this.latestVersion || !this.latestVersion.isNew;
  }

  get isValid() {
    if (this.items.length === 0) {
      return false;
    }

    return this.items.every(
      (version) => !version.canBeChanged || version.isValid,
    );
  }

  get latestVersion(): undefined | Version {
    if (this.items.length === 0) {
      return undefined;
    }

    return this.items.reduce(
      (latest, current) => (latest.id > current.id ? latest : current),
      this.items[0],
    );
  }

  get firstVersion(): undefined | Version {
    if (this.items.length === 0) {
      return undefined;
    }

    return this.items.reduce(
      (previous, current) => (previous.id < current.id ? previous : current),
      this.items[0],
    );
  }

  addEmptyVersion() {
    const newVersionName = increaseVersionName(this.latestVersion?.name);
    const newVersion = new Version({ name: newVersionName, isNew: true }, this);
    this.items.push(newVersion);
    this.ensureEmptyFileInTheBottom();
    return newVersion;
  }

  async deleteVersion(version: Version) {
    this.items = this.items.filter((v) => v !== version);
    if (!version.isNew) {
      await this.markVersionChanged(version);
    }
  }

  ensureEmptyFileInTheBottom() {
    this.items.forEach((version) => version.ensureEmptyFileInTheBottom());
  }

  generateFileId(): number {
    this.fileCounter += 1;
    return this.fileCounter;
  }

  getAllFiles(): VersionFile[] {
    return this.items.flatMap((version) => version.files);
  }

  getPreviousVersions(versionId: number): Version[] {
    return this.items.filter((ver) => ver.id < versionId);
  }

  getMatchedVersions(range: string): Version[] {
    return this.items.filter((version) => {
      const ver = semver.coerce(version.name, { loose: true });
      return ver && semver.satisfies(ver, range, { loose: true });
    });
  }

  getMatchedVersionsString(range: string): string {
    const matchedVersions = this.getMatchedVersions(range);

    if (matchedVersions.length === 0) {
      return 'none';
    }

    return matchedVersions.map((ver) => ver.name).join(', ');
  }

  getVariations(): string[] {
    const variations = this.getAllFiles()
      .map((file) => file.variation)
      .filter(Boolean);

    // eslint-disable-next-line unicorn/prefer-spread
    return Array.from(new Set(variations));
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  async markVersionChanged(_version: Version) {
    return this.onChange?.(this);
  }

  markAllAsSaved() {
    this.items.forEach((version) => version.markAsSaved());
  }

  toJSON(): VersionDto[] {
    return this.items.map((version) => version.toJSON());
  }

  updateAllIdIfNecessary() {
    this.items.forEach((version) => version.updateIdIfNecessary());
  }

  updateVariation(variation: string, newValue: string) {
    this.getAllFiles()
      .filter((file) => file.variation === variation)
      .forEach((file) =>
        file.handleChange({ name: 'variation', value: newValue }),
      );
  }
}

function increaseVersionName(versionName?: string): string {
  if (!versionName) {
    return '1.0.0';
  }

  const floatVersion = Number.parseFloat(versionName);
  if (Number.isNaN(floatVersion)) {
    return '1.0.0';
  }

  return `${(floatVersion + 0.1).toFixed(1)}.0`;
}
